import { PullRequestsRequest } from './generators/pullRequests';
import { selectors } from './selectors';
import URI from 'urijs';
import { call, put, select } from 'redux-saga/effects';
import { Organization } from 'models/Organization';
import { organizationSelector } from 'selectors/Organization';
import { fetchProjects } from 'sagas/projects';
import {
  CreateProjectStaticFilterRequest,
  DeleteProjectStaticFilterRequest,
  ListProjectStaticFiltersRequest,
} from './generators/staticFilters';
import { generators } from './generators';
import { selectors as routerSelectors } from 'stores/router/selectors';

export function pullRequestsPagesInitialURL({
  projectUUID,
  author,
  status,
  title,
  pageNumber,
}: PullRequestsRequest) {
  const uri = new URI(`/api/v1/projects/${projectUUID}/pull-requests`);
  uri.addQuery('status', status);
  if (author) {
    uri.addQuery('author', author);
  }
  if (title) {
    uri.addQuery('title', title);
  }
  if (pageNumber) {
    uri.addQuery('page', pageNumber);
  }
  return uri.toString();
}

function* refreshOrganizationProjects() {
  const organization: Organization | undefined = yield select(
    organizationSelector
  );
  if (organization) {
    yield fetchProjects({
      providerId: organization?.providerId,
      organizationSlug: organization?.slug,
    });
  }
}

export function getStaticFiltersPageURL({
  projectUUID,
  pageNumber,
}: ListProjectStaticFiltersRequest) {
  const uri = new URI(`api/v1/projects/${projectUUID}/static-filters`);
  uri.addQuery('type', 'multi');
  if (pageNumber) {
    uri.addQuery('page', pageNumber);
  }
  return uri.toString();
}

export const staticFiltersPages = generators.staticFilters.pages.customSaga({
  generatePage: getStaticFiltersPageURL,
});

export function* onCreateProjectStaticFilterSuccess(
  request: CreateProjectStaticFilterRequest
) {
  const pageNumber: number = yield select(routerSelectors.pageNumberSelector);
  yield put({
    type: 'PROJECTS/STATIC_FILTERS/CLEAR_TOAST',
  });
  yield put(
    generators.staticFilters.pages.request({
      projectUUID: request.projectUUID,
      pageNumber,
    })
  );
}

export const createStaticFilter = generators.staticFilters.create.saga(
  (api, request) =>
    call(api.post, `/api/v1/projects/${request.projectUUID}/static-filters`, {
      type: 'multi',
      engine: request.engine,
      issue_name: request.issueName,
      display_name: request.displayName,
      severity: request.severity,
    }),
  onCreateProjectStaticFilterSuccess
);

export function* onDeleteProjectStaticFilterSuccess(
  request: DeleteProjectStaticFilterRequest
) {
  const pageNumber: number = yield select(routerSelectors.pageNumberSelector);
  yield put({
    type: 'PROJECTS/STATIC_FILTERS/SHOW_TOAST',
    filter: request.filter,
  });
  yield put(
    generators.staticFilters.pages.request({
      projectUUID: request.projectUUID,
      pageNumber,
    })
  );
}

export const deleteStaticFilter = generators.staticFilters.delete.saga(
  (api, request) =>
    call(
      api.del,
      `/api/v1/projects/${request.projectUUID}/static-filters/${request.filterUUID}`
    ),
  onDeleteProjectStaticFilterSuccess
);

export function* sagas() {
  yield generators.pullRequests.pages.saga(
    pullRequestsPagesInitialURL,
    selectors.pullRequests.pages
  );
  yield staticFiltersPages;
  yield generators.commentPosting.toggle.saga(
    (api, request) =>
      call(
        api.post,
        `/api/v1/projects/${request.projectUUID}/toggle-comment-posting`,
        {
          enabled: request.isEnabled,
        }
      ),
    refreshOrganizationProjects
  );
  yield createStaticFilter;
  yield deleteStaticFilter;
}
